import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen, Brain, Briefcase, Building2, Calendar, Camera, Car, ChartColumn, CircleDollarSign, Code, Coffee, Cpu,
  Dumbbell, Eye, Flame, Folder, Footprints, Gamepad2, Gift, Globe, GraduationCap, Hammer, Heart, House, Leaf,
  Lightbulb, Lock, Mail, Megaphone, MessageCircle, Monitor, Music, Package, Palette, Plane, Shield, ShoppingCart,
  Smartphone, Sparkles, Star, Stethoscope, Target, Terminal, Truck, User, Users, Wrench, Zap,
} from "lucide-react";
import { iconColor, iconSymbol } from "@desk/design";
import { cn } from "@/lib/utils";

/** lucide glyphs for the SF Symbol keys in @desk/design ICON_SYMBOLS. */
export const LUCIDE: Record<string, LucideIcon> = {
  BookOpen, Brain, Briefcase, Building2, Calendar, Camera, Car, ChartColumn, CircleDollarSign, Code, Coffee, Cpu,
  Dumbbell, Eye, Flame, Folder, Footprints, Gamepad2, Gift, Globe, GraduationCap, Hammer, Heart, House, Leaf,
  Lightbulb, Lock, Mail, Megaphone, MessageCircle, Monitor, Music, Package, Palette, Plane, Shield, ShoppingCart,
  Smartphone, Sparkles, Star, Stethoscope, Target, Terminal, Truck, User, Users, Wrench, Zap,
};

export const symbolGlyph = (sf: string | null | undefined): LucideIcon | undefined => {
  const s = iconSymbol(sf);
  return s ? LUCIDE[s.lucide] : undefined;
};

/**
 * A preset symbol on a soft tinted tile. Tile and glyph tones swap with the app theme
 * through CSS variables, so the same markup works in light and dark.
 */
export function SymbolIcon({
  sf,
  color,
  theme,
  className,
}: {
  sf: string;
  color: string | null | undefined;
  /** Force one theme's tones (previews); by default they follow the app theme. */
  theme?: "light" | "dark";
  className?: string;
}) {
  const Glyph = symbolGlyph(sf);
  if (!Glyph) return null;
  const c = iconColor(color);
  const light = theme === "dark" ? c.dark : c.light;
  const dark = theme === "light" ? c.light : c.dark;
  const style = {
    "--icon-fg": light.fg,
    "--icon-tile": light.tile,
    "--icon-fg-dark": dark.fg,
    "--icon-tile-dark": dark.tile,
  } as CSSProperties;
  return (
    <span
      aria-hidden
      data-icon-symbol={sf}
      style={style}
      className={cn(
        "inline-flex size-[18px] shrink-0 items-center justify-center rounded-[28%]",
        "bg-[var(--icon-tile)] text-[var(--icon-fg)] dark:bg-[var(--icon-tile-dark)] dark:text-[var(--icon-fg-dark)]",
        className,
      )}
    >
      <Glyph className="size-[66%]" strokeWidth={2.25} />
    </span>
  );
}
